import { apiFetchJson, apiGet } from './apiClient';

export type SubscriptionAccessSummary = {
  industries: string[];
  sectors: string[];
  companies: string[];
  gradeLevels: string[];
  packages: string[];
  aiAccess: { mode: 'unlimited' | 'limited'; limit?: number } | null;
};

export type AdminSubscriptionRule = {
  id: number;
  effect: string;
  dimension: string;
  operator: string;
  valuesJson: Record<string, unknown>;
  priority: number;
  isActive: boolean;
  note: string | null;
};

export type AdminSubscription = {
  id: number;
  code: string;
  name: string;
  description: string | null;
  status: string;
  validFrom: string | null;
  validTo: string | null;
  createdAt: string;
  updatedAt: string;
  assignmentsCount: number;
  rules: AdminSubscriptionRule[];
  accessSummary: SubscriptionAccessSummary;
};

export type AdminSubscriptionAssignment = {
  id: number;
  status: string;
  assignedAt: string;
  revokedAt: string | null;
  subscription: {
    id: number;
    code: string;
    name: string;
    status: string;
  };
};

export type AdminSubscriptionUser = {
  id: string;
  email: string;
  login: string | null;
  displayName: string | null;
  firstName: string | null;
  lastName: string | null;
  isActive: boolean;
  createdAt: string;
  lastLoginAt: string | null;
  aiTrialUsed: number;
  isAdmin: boolean;
  isAnalystLegacy: boolean;
  canEditRecords: boolean;
  canGradeRecords: boolean;
  roleCodes: string[];
  accountType: 'administrator' | 'user';
  assignments: AdminSubscriptionAssignment[];
  accessSummaries: Array<{
    subscriptionId: number;
    subscriptionName: string;
    summary: SubscriptionAccessSummary;
  }>;
};

export type SubscriptionCatalog = {
  industries: Array<{ id: number; name: string; packageName: string }>;
  sectors: Array<{ id: number; name: string; industryId: number; industryName: string }>;
  companies: Array<{ id: string; name: string; sectorId: number; sectorName: string; industryId: number; industryName: string }>;
  gradeLevels: number[];
};

export type CreateSubscriptionPayload = {
  name: string;
  code?: string;
  description?: string;
  status?: string;
  validFrom?: string | null;
  validTo?: string | null;
  dataScope: {
    industries: string[];
    sectors: string[];
    companies: string[];
    gradeLevels: string[];
  };
  aiAccess: {
    enabled: boolean;
    mode?: 'unlimited' | 'limited';
    limit?: number;
  };
};

export async function fetchAdminSubscriptionCatalog(): Promise<SubscriptionCatalog> {
  const res = await apiGet<SubscriptionCatalog>('/admin/subscriptions/catalog');
  if (!res) {
    throw new Error('Не удалось загрузить справочник подписок');
  }
  return res;
}

export async function fetchAdminSubscriptionUsers(params?: {
  search?: string;
  page?: number;
  limit?: number;
}): Promise<{ data: AdminSubscriptionUser[]; total: number; page: number; limit: number }> {
  const query = new URLSearchParams();
  if (params?.search) query.set('search', params.search);
  if (params?.page) query.set('page', String(params.page));
  if (params?.limit) query.set('limit', String(params.limit));
  const suffix = query.toString() ? `?${query.toString()}` : '';
  const res = await apiGet<{ data: AdminSubscriptionUser[]; total: number; page: number; limit: number }>(
    `/admin/subscriptions/users${suffix}`,
  );
  return {
    data: res?.data || [],
    total: res?.total || 0,
    page: res?.page || 1,
    limit: res?.limit || 25,
  };
}

export async function fetchAdminSubscriptions(): Promise<AdminSubscription[]> {
  const res = await apiGet<{ data: AdminSubscription[] }>('/admin/subscriptions');
  return res?.data || [];
}

export async function createAdminSubscription(payload: CreateSubscriptionPayload): Promise<AdminSubscription> {
  const res = await apiFetchJson<AdminSubscription>('POST', '/admin/subscriptions', { body: payload });
  if (!res.ok) throw new Error(res.error);
  return res.data as AdminSubscription;
}

export async function assignAdminSubscription(subscriptionId: number, userId: string) {
  const res = await apiFetchJson('POST', `/admin/subscriptions/${subscriptionId}/assign`, {
    body: { userId },
  });
  if (!res.ok) throw new Error(res.error);
  return res.data;
}

export async function revokeAdminSubscriptionAssignment(assignmentId: number) {
  const res = await apiFetchJson('POST', `/admin/subscriptions/assignments/${assignmentId}/revoke`);
  if (!res.ok) throw new Error(res.error);
  return res.data;
}

export function formatAdminSubscriptionUserLabel(user: AdminSubscriptionUser): string {
  const name = [user.lastName, user.firstName].filter(Boolean).join(' ').trim();
  if (name && user.email) return `${name} (${user.email})`;
  return user.displayName || user.email || user.login || user.id;
}

export function formatAccessSummary(summary: SubscriptionAccessSummary): string {
  const parts: string[] = [];
  if (summary.industries.length) parts.push(`индустрии: ${summary.industries.length}`);
  if (summary.sectors.length) parts.push(`сектора: ${summary.sectors.length}`);
  if (summary.companies.length) parts.push(`компании: ${summary.companies.length}`);
  if (summary.gradeLevels.length) parts.push(`грейды: ${summary.gradeLevels.join(', ')}`);
  if (summary.aiAccess?.mode === 'unlimited') parts.push('AI: безлимит');
  if (summary.aiAccess?.mode === 'limited') parts.push(`AI: ${summary.aiAccess.limit ?? 0} запросов`);
  return parts.length ? parts.join('; ') : '—';
}
